/**
 * PhaseNameEditorTab Component
 *
 * Tab for editing custom phase names.
 * Features inline editing with preview of how names appear.
 *
 * Part of the AdminEditorModal.
 */

import React, { useMemo } from 'react';
import { RotateCcw, Type } from 'lucide-react';
import { InlineEdit } from '../shared/InlineEdit';
import { useAdminEditorStore } from '../../store/adminEditorStore';
import {
  DEFAULT_PHASE_NAMES,
  PHASE_ORDER,
  getPhaseDisplayName,
} from '../../constants/defaults';
import type { PhaseType, CustomPhaseNames } from '../../types/adminEditor';
import { cn } from '@/shared/utils/cn';

export interface PhaseNameEditorTabProps {
  /** Additional CSS classes */
  className?: string;
}

/**
 * PhaseNameCard - Card for editing a single phase name
 */
interface PhaseNameCardProps {
  phaseType: PhaseType;
  customName: string | undefined;
  onNameChange: (newName: string) => void;
}

const PhaseNameCard: React.FC<PhaseNameCardProps> = ({
  phaseType,
  customName,
  onNameChange,
}) => {
  const defaultName = DEFAULT_PHASE_NAMES[phaseType];
  const displayName = customName || defaultName;
  const isCustomized = !!customName && customName !== defaultName;

  const handleSave = (newValue: string) => {
    // If the value is empty or same as default, clear the custom name
    if (!newValue || newValue === defaultName) {
      onNameChange('');
    } else {
      onNameChange(newValue);
    }
  };

  return (
    <div className="p-4 bg-dark-surface border border-dark-border/30 rounded-xl space-y-3">
      {/* Phase type badge */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-dark-muted uppercase tracking-wide">
          {phaseType.replace('-', ' ')}
        </span>
        {isCustomized && (
          <span className="text-xs text-brand-400 bg-brand-500/10 px-2 py-0.5 rounded">
            Customized
          </span>
        )}
      </div>

      {/* Editable name */}
      <div className="flex items-center gap-3">
        <Type size={18} className="text-dark-muted flex-shrink-0" />
        <InlineEdit
          value={displayName}
          onSave={handleSave}
          placeholder={defaultName}
          maxLength={30}
          className="flex-1 py-1"
          textClassName="text-lg font-medium"
          validate={(value) => {
            if (value.length < 2) {
              return 'Name must be at least 2 characters';
            }
            return null;
          }}
        />
      </div>

      {/* Default hint */}
      <p className="text-xs text-dark-muted">
        Default: <span className="text-dark-text">{defaultName}</span>
      </p>
    </div>
  );
};

/**
 * PhaseNameEditorTab - Edit phase display names
 *
 * Features:
 * - Inline editable text for each phase name
 * - Preview of how names appear
 * - Reset to defaults button
 * - Shows which names are customized
 */
export const PhaseNameEditorTab: React.FC<PhaseNameEditorTabProps> = ({
  className = '',
}) => {
  // Store state and actions
  const customPhaseNames = useAdminEditorStore((state) => state.customPhaseNames);
  const setPhaseNames = useAdminEditorStore((state) => state.setPhaseNames);

  // Count customized names
  const customizedCount = useMemo(() => {
    return Object.entries(customPhaseNames).filter(
      ([type, name]) =>
        name && name !== DEFAULT_PHASE_NAMES[type as PhaseType]
    ).length;
  }, [customPhaseNames]);

  const handleNameChange = (phaseType: PhaseType, newName: string) => {
    const newNames: CustomPhaseNames = { ...customPhaseNames };

    if (!newName || newName === DEFAULT_PHASE_NAMES[phaseType]) {
      // Remove custom name if it's empty or same as default
      delete newNames[phaseType];
    } else {
      newNames[phaseType] = newName;
    }

    setPhaseNames(newNames);
  };

  const handleResetToDefaults = () => {
    setPhaseNames({});
  };

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header with stats and reset button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-dark-muted">
          {customizedCount > 0 ? (
            <span className="text-brand-400">{customizedCount} customized</span>
          ) : (
            <span>Using default names</span>
          )}
        </div>
        <button
          type="button"
          onClick={handleResetToDefaults}
          disabled={customizedCount === 0}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-all duration-150',
            customizedCount > 0
              ? 'bg-dark-surface/50 hover:bg-dark-active text-dark-muted hover:text-dark-text'
              : 'bg-dark-surface text-dark-muted opacity-50 cursor-not-allowed'
          )}
          title="Reset to default names"
        >
          <RotateCcw size={14} />
          Reset to Defaults
        </button>
      </div>

      {/* Instructions */}
      <p className="text-xs text-dark-muted">
        Click on a phase name to customize it. Leave empty or click away to use the
        default name.
      </p>

      {/* Phase name cards */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {PHASE_ORDER.map((phaseType) => (
          <PhaseNameCard
            key={phaseType}
            phaseType={phaseType}
            customName={customPhaseNames[phaseType]}
            onNameChange={(newName) => handleNameChange(phaseType, newName)}
          />
        ))}
      </div>

      {/* Preview section */}
      <div className="pt-4 border-t border-dark-border/30">
        <h4 className="text-xs font-medium text-dark-muted uppercase tracking-wide mb-3">
          Preview
        </h4>
        <div className="flex flex-wrap gap-2">
          {PHASE_ORDER.map((phaseType) => (
            <span
              key={phaseType}
              className="px-3 py-1.5 text-sm bg-dark-surface/50 border border-dark-border/30 rounded text-dark-text"
            >
              {getPhaseDisplayName(phaseType, customPhaseNames)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PhaseNameEditorTab;
